import { useTranslation } from 'react-i18next';
import Battery1BarRounded from '@mui/icons-material/Battery1BarRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import CloudOffOutlined from '@mui/icons-material/CloudOffOutlined';
import CloudOutlined from '@mui/icons-material/CloudOutlined';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import Constants from 'style/constants';

import { MonitoringType } from './monitoring-type';

type Props = {
  type: MonitoringType;
  withTooltip: boolean;
  visible: boolean;
  size: number;
};

const MonitoringIcon = ({ type, withTooltip, visible, size }: Readonly<Props>): React.JSX.Element => {
  const { t } = useTranslation('monitoring');

  const getTooltipText = (): string | undefined => {
    switch (type) {
      case MonitoringType.Offline:
        return t('messages.offline');
      case MonitoringType.Online:
        return t('messages.online');
      case MonitoringType.BatteryCritical:
        return t('messages.batteryCritical');
      case MonitoringType.BatteryLow:
        return t('messages.batteryLow');
      case MonitoringType.OutDated:
        return t('messages.outDated');
      case MonitoringType.GatewayOffline:
        return t('messages.gatewayOffline');
      case MonitoringType.GatewayOnline:
        return t('messages.gatewayOnline');
      case MonitoringType.NotMonitored:
        return t('messages.notMonitored');
      case MonitoringType.PassiveGatewayOffline:
        return t('messages.passiveGatewayOffline');
      case MonitoringType.GatewayServiceOffline:
        return t('messages.gatewayServiceOffline');
      case MonitoringType.SystemOffline:
        return t('messages.systemOffline');
      case MonitoringType.SystemOnline:
        return t('messages.systemOnline');
      case MonitoringType.SystemWarning:
        return t('messages.systemWarning');
      case MonitoringType.SystemMalfunction:
        return t('messages.systemMalfunction');
      case MonitoringType.GatewayNotMonitored:
        return t('messages.gatewayNotMonitored');
      case MonitoringType.DeviceMalfunction:
        return t('messages.deviceMalfunction');
      case MonitoringType.ConnectorOffline:
        return t('messages.connectorOffline');
      case MonitoringType.ConnectorOnline:
        return t('messages.connectorOnline');
      default:
        return undefined;
    }
  };

  const getSxProperty = (color: string): { color: string; width: number; height: number } => ({
    color: visible ? color : 'transparent',
    width: size,
    height: size,
  });

  const getIcon = (): React.JSX.Element => {
    switch (type) {
      case MonitoringType.BatteryCritical:
        return <Battery1BarRounded sx={getSxProperty(Constants.Colors.error)} />;
      case MonitoringType.BatteryLow:
      case MonitoringType.BatteryCombined:
        return <Battery1BarRounded sx={getSxProperty(Constants.Colors.brightOrange)} />;
      case MonitoringType.OutDated:
      case MonitoringType.SystemMalfunction:
        return <WarningAmberRounded sx={getSxProperty(Constants.Colors.error)} />;
      case MonitoringType.GatewayOffline:
      case MonitoringType.Offline:
      case MonitoringType.SystemOffline:
      case MonitoringType.ConnectorOffline:
        return <CloudOffOutlined sx={getSxProperty(Constants.Colors.error)} />;
      case MonitoringType.Online:
      case MonitoringType.GatewayOnline:
      case MonitoringType.SystemOnline:
      case MonitoringType.ConnectorOnline:
        return <CloudOutlined sx={getSxProperty(Constants.Colors.success)} />;
      case MonitoringType.NotMonitored:
      case MonitoringType.GatewayNotMonitored:
        return <CloseRounded sx={getSxProperty(Constants.Colors.onSurfaceVariant)} />;
      case MonitoringType.GatewayServiceOffline:
        return <CloudOffOutlined sx={getSxProperty(Constants.Colors.brightOrange)} />;
      case MonitoringType.SystemWarning:
      case MonitoringType.PassiveGatewayOffline:
      case MonitoringType.DeviceMalfunction:
        return <WarningAmberRounded sx={getSxProperty(Constants.Colors.brightOrange)} />;
      default:
        return <></>;
    }
  };

  return withTooltip && visible ? (
    <Tooltip
      title={getTooltipText()}
      followCursor
      slots={{
        transition: Zoom,
      }}
    >
      {getIcon()}
    </Tooltip>
  ) : (
    getIcon()
  );
};

export default MonitoringIcon;
